<template>
 <!-- compact 모드 (ChatPub 사이드바) -->
 <div v-if="compact" class="px-3 py-3 overflow-y-auto flex flex-col gap-2">
  <div class="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-50 transition-colors">
   <Checkbox
     label="전체선택"
     class="font-semibold text-[11px] text-gray-500"
     v-model="checkedAll"
     @update:model-value="updateAllCheck"
   />
  </div>

  <div
    v-for="project in filteredAndSortedProjects"
    :key="`${project.id}-${project.created_at}`"
    class="rounded-lg border border-gray-100 bg-gray-50/50 overflow-hidden"
  >
   <div
     class="flex items-center justify-between px-2.5 py-2 cursor-pointer hover:bg-gray-100/60 transition-colors"
     @click.stop="toggleChildren(project.id)"
   >
    <span class="text-[11px] leading-snug break-words whitespace-normal flex-1 min-w-0 text-gray-700 font-medium" :title="project.name">
      {{ project.name }}
    </span>
    <svg
      v-if="project?.crawling_jobs?.length"
      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
      class="flex-shrink-0 ml-1 text-gray-400 transition-transform duration-200"
      :class="{ 'rotate-180': openItem[project.id] }"
    >
      <polyline points="6 9 12 15 18 9"/>
    </svg>
   </div>

   <div
     v-if="project?.crawling_jobs?.length && openItem[project.id]"
     class="border-t border-gray-100 bg-white"
   >
     <div
      v-for="child in project.crawling_jobs"
      :key="child.id"
      class="flex items-center px-3 py-1.5 hover:bg-violet-50/50 transition-colors cursor-pointer"
      @click="toggleChild(project, child.id)"
    >
      <Checkbox
        :value="child.id"
        v-model="checkedChildren[project.id]"
        @update:model-value="(checked) => updateChildCheck(project, checked)"
        class="text-[11px] flex-shrink-0"
      />
      <input
        v-if="editingChildId === child.id"
        type="text"
        v-model="editedJobName"
        @keyup.enter="saveJobName(child.id, project.id)"
        @blur="cancelEdit"
        @click.stop
        class="ml-1.5 border border-gray-200 rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:border-violet-300 w-full"
        autofocus
      />
      <span
        v-else
        @dblclick.stop="startEdit(child.id, child.job_name || formatCrawlingJob(child))"
        class="ml-1.5 text-[11px] text-gray-600 leading-tight break-words whitespace-normal"
        :title="child.job_name || formatCrawlingJob(child)"
      >
        {{ child.job_name || formatCrawlingJob(child) }}
      </span>
     </div>
   </div>
  </div>
 </div>

 <!-- 기본 모드 (메인페이지) -->
 <ul v-else class="h-[calc(100%-56px)] px-[10px] py-5 overflow-y-auto flex flex-col gap-[6px]">
  <li class="text-left">
   <Checkbox
     label="전체선택"
     class="font-bold text-xs"
     v-model="checkedAll"
     @update:model-value="updateAllCheck"
   />
  </li>
  <li
    v-for="project in filteredAndSortedProjects"
    :key="`${project.id}-${project.created_at}`"
  >
   <div class="flex items-center justify-between">
    <Checkbox
      :label="`${project.name}(ProjectNo.${project.id})`"
      :value="project.id"
      v-model="checkedParents"
      @update:model-value="(checked) => updateParentCheck(project, checked)"
      @onClick="$emit('onClick', project)"
      class="text-xs break-words whitespace-normal"
      :title="project.name"
    />
    <button
      v-if="project?.crawling_jobs?.length"
      @click.stop="toggleChildren(project.id)"
      class="w-6 h-6 flex items-center justify-center flex-shrink-0"
    >
     <img
       :src="openItem[project.id] ? minus : plus"
       alt="toggle"
       class="w-4 h-4 object-contain flex-shrink-0 m-0"
       style="transform: translateX(0) translateY(0);"
     />
    </button>
   </div>
   <ol
     v-if="project?.crawling_jobs?.length && openItem[project.id]"
     class="ml-[18px]"
   >
     <li
      v-for="child in project.crawling_jobs"
      :key="child.id"
      class="text-left"
    >
     <div class="flex items-center">
      <Checkbox
        :value="child.id"
        v-model="checkedChildren[project.id]"
        @update:model-value="(checked) => updateChildCheck(project, checked)"
        class="text-xs"
      />
      <input
        v-if="editingChildId === child.id"
        type="text"
        v-model="editedJobName"
        @keyup.enter="saveJobName(child.id, project.id)"
        @blur="cancelEdit"
        class="ml-2 border rounded px-2 py-1 text-xs focus:outline-none"
        autofocus
      />
      <span
        v-else
        @click="toggleChild(project, child.id)"
        @dblclick="startEdit(child.id, child.job_name || formatCrawlingJob(child))"
        class="cursor-pointer ml-2 text-xs break-words whitespace-normal"
        :title="child.job_name || formatCrawlingJob(child)"
      >
        {{ child.job_name || formatCrawlingJob(child) }}
      </span>
     </div>
    </li>
   </ol>
  </li>
 </ul>
</template>

<script setup>
import { reactive, ref, watch, computed, onMounted } from 'vue';
import plus from '@/assets/plus.svg'
import minus from '@/assets/minus.svg'
import Checkbox from '@/components/ui/Checkbox.vue';
import store from '@/store';

const props = defineProps({
 projects: {
  type: Array,
  default: () => ([]),
 },
 parents: {
  type: Array,
  default: () => ([]),
 },
 children: {
  type: Object,
  default: () => ({}),
 },
 sortOption: {
  type: String,
  default: 'name'
 },
 filterText: {
  type: String,
  default: ''
 },
 compact: {
  type: Boolean,
  default: false
 },
})

function formatCrawlingJob(job) {
 //const requesterId = job.lucy_path.split('/')[0]
 const requesterId = job.user_name
 console.log(job);
 const startDate = new Date(job.created_at)
 const formattedDate = `${startDate.getFullYear()}.${String(startDate.getMonth() + 1).padStart(2, '0')}.${String(startDate.getDate()).padStart(2, '0')}`
 const status = job.status === 'COMPLETED' ? '완료' :
    job.status === 'INITIALIZED' ? '초기화' :
    job.status === 'CREATED' ? '생성 중' :
    job.status === 'PROCESSING' ? '크롤링 중' :
    'failure'  

 return job.job_name ? job.job_name : `${requesterId}_${formattedDate}_${status}`
}

const checkedParents = ref(props.parents)
const checkedChildren = reactive(props.children);
const openItem = reactive({})

watch(() => props.projects, (val) => {
 Object.assign(
  openItem,
  val.reduce((acc, cur) => {
   if (cur?.crawling_jobs?.length) {
    acc[cur.id] = props.compact ? true : false;
   }
   return acc;
  }, {})
 );
}, { immediate: true })

function toggleChildren(id) {
 openItem[id] = !openItem[id];
}

function toggleChild(project, childId) {
 const arr = checkedChildren[project.id] || [];
 if (arr.includes(childId)) {
  checkedChildren[project.id] = arr.filter(id => id !== childId);
 } else {
  checkedChildren[project.id] = [...arr, childId];
 }
 updateChildCheck(project, checkedChildren[project.id]);
}

const checkedAll = ref(false)
const emits = defineEmits(['update:parents', 'update:children'])

function updateAllCheck(checked) {
 checkedParents.value = checked
  ? props.projects.map((project) => project.id)
  : [];

 for (const key in checkedChildren) {
  checkedChildren[key] = checked
   ? props.projects.find((project) => +project.id === +key).crawling_jobs?.map((child) => child.id) || []
   : [];
 }
}

function updateSelectAll() {
 const allSelected = props.projects.every((project) => {
  const parentChecked = checkedParents.value.includes(project.id);
  const childrenChecked = !project.crawling_jobs?.length
   || checkedChildren[project.id]?.length === project.crawling_jobs.length;
  return parentChecked && childrenChecked;
 });

 checkedAll.value = allSelected;
 emits('update:children', checkedChildren.value)
 emits('update:parents', checkedParents.value)
}

function updateParentCheck(project, checked) {
 checkedChildren[project.id] = checked.includes(project.id)
  ? project.crawling_jobs?.map((child) => child.id) || [] : [];

 updateSelectAll();
}

function updateChildCheck(project, checked) {
 const { id } = project;
 const childrenIds = project?.crawling_jobs.map((child) => child.id) || [];
 const selectedChildren = checkedChildren[id] || [];

 if (childrenIds.length === selectedChildren.length) {
  if (!checkedParents.value.includes(id)) checkedParents.value.push(id);
 } else {
  checkedParents.value = checkedParents.value.filter(
   (parentId) => parentId !== id
  );
 }

 updateSelectAll();
}

const filteredAndSortedProjects = computed(() => {
 const uniqueProjects = Array.from(
  new Map(props.projects.map(project => [project.id, project])).values()
 );
 
 let result = uniqueProjects;
 
 if (props.filterText) {
  result = result.filter(project => 
   project.name.toLowerCase().includes(props.filterText.toLowerCase())
  );
 }
 
 return result.sort((a, b) => {
  if (props.sortOption === 'name') {
   return a.name.localeCompare(b.name);
  } else if (props.sortOption === 'date') {
   return new Date(a.created_at) - new Date(b.created_at);
  }
  return 0;
 });
})

const editingChildId = ref(null);
const editedJobName = ref('');

function startEdit(childId, currentName) {
  editingChildId.value = childId;
  editedJobName.value = currentName;
}

function cancelEdit() {
  editingChildId.value = null;
  editedJobName.value = '';
}

async function saveJobName(childId, projectId) {
  if (editedJobName.value.trim() === '') {
    cancelEdit();
    return;
  }

  try {
    const result = await store.dispatch('crawling/updateCrawlingJobName', {
      jobId: childId,
      jobName: editedJobName.value
    });
    if (result && result.success) {
      const project = props.projects.find(p => p.id === projectId);
      if (project) {
        const job = project.crawling_jobs.find(j => j.id === childId);
        if (job) {
          job.job_name = editedJobName.value;
        }
      }
      cancelEdit();
    } else {
      throw new Error('업데이트 실패');
    }
  } catch (error) {
    console.error('Failed to update job name:', error);
    cancelEdit();
  }
}

onMounted(() => {
  document.addEventListener('update:model-value', (event) => {
    if (event.value === false) {
      checkedAll.value = false;
      updateAllCheck(false);
    }
  });
});
</script>
